import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AddOrderHeaderRequest, OrderHeaderResponse, OrderLine, OrderRecommendationsResponse } from './item-details.models';
import { environment } from '../../environments/environment';
import { apiTrace } from '../shared/api-trace';
import { getAuthHttpOptions } from '../utils/auth-request.util';

export type HeaderTextTargetRequest = {
    documentClass: string;
    documentName: string;
    text: string;
};

type PendingTextEntry = {
    lineNo?: string;
    textType: string;
    targets: { key: string; label: string; text: string }[];
    /** True when Pre-Text was auto-queued from Extra information (uca2). */
    fromExtraInfo?: boolean;
};

@Injectable({
    providedIn: 'root'
})
export class ItemDetailsService {
    private baseUrl = environment.apiUrl;
    private pendingHeaderTextEntries: PendingTextEntry[] = [];
    private pendingLineTextEntries: PendingTextEntry[] = [];

    constructor(private http: HttpClient) {}

    getOrderHeader(orderNumber: string, company: string, division: string, orderType: string): Observable<OrderHeaderResponse> {
        const url = `${this.baseUrl}Order/GetOrderHeader?orderNumber=${encodeURIComponent(orderNumber)}&company=${company}&division=${division}&orderType=${orderType}`;
        return this.http.get<OrderHeaderResponse>(url).pipe(catchError((err) => throwError(() => err)));
    }

    getOrderLines(orderNumber: string, company: string, division: string): Observable<{ content: OrderLine[] }> {
        const url = `${this.baseUrl}OrderLine/GetListOrderLinesPagination?orderNumber=${encodeURIComponent(orderNumber)}&company=${company}&division=${division}&pageNumber=0&pageSize=10000&sortBy=ASC&orderType=CustomerOrderLine`;
        return this.http.get<{ content: OrderLine[] }>(url).pipe(catchError((err) => throwError(() => err)));
    }

    getOldSalesPrices(
        company: string,
        division: string,
        warehouse: string,
        customer: string,
        itemNo: string,
        maxRecords: number
    ): Observable<any> {
        const params = new URLSearchParams();
        params.set('itemNo', (itemNo || '').trim());
        params.set('customer', (customer || '').trim());
        params.set('division', (division || '').trim());
        params.set('company', company || '');
        params.set('maxRecords', String(maxRecords));
        const wh = (warehouse || '').trim();
        if (wh) {
            params.set('warehouse', wh);
        }
        const url = `${this.baseUrl}m3/item/old-sales-price?${params.toString()}`;
        return this.http.get<any>(url).pipe(catchError((err) => throwError(() => err)));
    }

    /** Recent sales of one item across customers. Customer is not sent. */
    getItemLastSales(company: string, division: string, itemNo: string, maxRecords: number): Observable<any> {
        const params = new URLSearchParams();
        params.set('itemNo', (itemNo || '').trim());
        params.set('division', (division || '').trim());
        params.set('company', company || '');
        params.set('maxRecords', String(maxRecords));
        const url = `${this.baseUrl}m3/item/item-last-sales?${params.toString()}`;
        return this.http.get<any>(url).pipe(catchError((err) => throwError(() => err)));
    }

    /** Open Torna orders (CRT, PKL, WIP) still holding this item, with line quantity. */
    getItemOpenOrders(company: string, division: string, itemNo: string): Observable<any> {
        const params = new URLSearchParams();
        params.set('itemNo', (itemNo || '').trim());
        params.set('division', (division || '').trim());
        params.set('company', company || '');
        const url = `${this.baseUrl}m3/item/item-open-orders?${params.toString()}`;
        return this.http.get<any>(url).pipe(catchError((err) => throwError(() => err)));
    }

    getItemStockLots(company: string, division: string, warehouse: string, itemNo: string): Observable<any> {
        const url =
            `${this.baseUrl}m3/item/stock-lots` +
            `?itemNo=${encodeURIComponent(itemNo)}` +
            `&warehouse=${encodeURIComponent(warehouse)}` +
            `&company=${encodeURIComponent(company)}` +
            `&division=${encodeURIComponent(division)}`;
        return this.http.get<any>(url).pipe(catchError((err) => throwError(() => err)));
    }

    getOrderRecommendations(
        orderNumber: string | undefined,
        company: string,
        division: string,
        orderType: string,
        limit?: number,
        excludeItemNos?: string[],
        customerId?: string,
        traceReason?: string
    ): Observable<OrderRecommendationsResponse> {
        const exclude = (excludeItemNos || []).map((x) => String(x).trim()).filter(Boolean);
        apiTrace('Recommendations', traceReason || 'UNKNOWN', {
            company,
            division,
            orderNumber: (orderNumber || '').trim() || undefined,
            customerId: (customerId || '').trim() || undefined,
            limit: limit ?? undefined,
            excludeCount: exclude.length
        });
        const params = new URLSearchParams();
        const trimmedOrder = (orderNumber || '').trim();
        if (trimmedOrder) {
            params.set('orderNumber', trimmedOrder);
        }
        params.set('company', company || '');
        params.set('division', division || '');
        params.set('orderType', orderType || 'CustomerOrder');
        const cid = (customerId || '').trim();
        if (cid) {
            params.set('customerId', cid);
        }
        if (limit != null && limit > 0) {
            params.set('limit', String(limit));
        }
        if (exclude.length) {
            params.set('excludeItemNos', exclude.join(','));
        }
        const url = `${this.baseUrl}Order/GetOrderRecommendations?${params.toString()}`;
        return this.http.get<OrderRecommendationsResponse>(url).pipe(catchError((err) => throwError(() => err)));
    }

    getItemDetails(company: string, division: string, body: any, traceReason?: string): Observable<any> {
        apiTrace('ItemDetails', traceReason || 'UNKNOWN', {
            company,
            division,
            itno: body?.itno ?? body?.itemNo ?? body?.itemNumber,
            cuno: body?.cuno ?? body?.customerId,
            whlo: body?.whlo ?? body?.whsId ?? body?.warehouse
        });
        const url = `${this.baseUrl}m3/item/itemdetails?company=${encodeURIComponent(company)}&division=${encodeURIComponent(division)}`;
        return this.http.post<any>(url, body).pipe(
            map((response) => {
                // Integrate ItemDetails fields directly into the response for easy mapping
                let ItemDetails: any = {};
                if (response && response.ItemDetails) {
                    ItemDetails = {
                        pcs: response.ItemDetails.pcs,
                        size: response.ItemDetails.size,
                        vatCode: response.ItemDetails.vatCode,
                        netWeight: response.ItemDetails.netWeight,
                        grossWeight: response.ItemDetails.grossWeight
                    };
                }
                if (!response || !response.lstVatPercentag) return response;
                const warehouse = String(body?.whlo ?? body?.whsId ?? body?.warehouse ?? '').trim();
                const vatFields = resolveVatFieldsByWarehouse(response.lstVatPercentag, warehouse);
                return { ...response, vatFields };
            }),
            catchError((err) => throwError(() => err))
        );
    }

    /**
     * Lean available stock (Lst_CO_ln_Scrp + reserved only).
     * Used by V2 recommendation cards and qty-change stock refresh.
     * Body: { itno, whlo } — cuno/orno ignored.
     */
    getAvailableStock(
        company: string,
        division: string,
        body: { itno: string; whlo: string; cuno?: string },
        traceReason?: string
    ): Observable<any> {
        apiTrace('AvailableStock', traceReason || 'UNKNOWN', {
            company,
            division,
            itno: body?.itno,
            whlo: body?.whlo,
            cuno: body?.cuno
        });
        const url = `${this.baseUrl}m3/item/available-stock?company=${encodeURIComponent(company)}&division=${encodeURIComponent(division)}`;
        return this.http.post<any>(url, body).pipe(catchError((err) => throwError(() => err)));
    }

    searchItemNames(query: string, company: string, division: string, warehouse: string): Observable<any[]> {
        console.log('Searching item names with query:', query,"warehouse:",warehouse);
        // const url= `http://192.168.31.145:8444/m3/item/search?SQRY=WHLO%3A400%20related%3A%5BITEM_DESC(%22hEE*%22)%5D&company=781&division=400&warehouse=400`;
        // working 
        const url= `${this.baseUrl}m3/item/search?SQRY=WHLO%3A${warehouse}%20  STAT%3A20%20related%3A%5BITEM_DESC(%22${query}%22)%5D&company=${company}&division=${division}&warehouse=${warehouse}`;
        // working
        // const url= `http://192.168.31.145:8444/m3/item/search?WHLO:${encodeURIComponent(warehouse)} related:[ITEM_DESC("${encodeURIComponent(query)}")] STAT:${encodeURIComponent('20')}&company=${encodeURIComponent(company)}&division=${encodeURIComponent(division)}`;

        // const url = `${this.baseUrl}m3/item/search?SQRY=FUDS:${encodeURIComponent(query + '*')} STAT:${encodeURIComponent('20')}&company=${encodeURIComponent(company)}&division=${encodeURIComponent(division)}`;
        // const url = `${this.baseUrl}m3/item/search?SQRY=WHLO:${encodeURIComponent(warehouse)} related:[ITEM_DESC("${encodeURIComponent(query)}")] STAT:${encodeURIComponent('20')}&company=${encodeURIComponent(company)}&division=${encodeURIComponent(division)}`;
// const url = `${this.baseUrl}m3/item/search?SQRY=WHLO:${encodeURIComponent(warehouse)}%20related:[ITEM("${encodeURIComponent(query)}")] STAT:${encodeURIComponent('20')} &company=${encodeURIComponent(company)}&division=${encodeURIComponent(division)}`;

        // const url = `http://192.168.31.92:8444/m3/item/search?SQRY=FUDS:${encodeURIComponent(query + '*')} STAT:${encodeURIComponent('20')}&company=${encodeURIComponent(company)}&division=${encodeURIComponent(division)}`;
        return this.http.get<any[]>(url).pipe(catchError((err) => throwError(() => err)));
    }

    searchItemNumbers(query: string, company: string, division: string, warehouse: string): Observable<any[]> {
        console.log('Searching item numbers with query:', query,"warehouse:",warehouse);
        
        // const url = `${this.baseUrl}m3/item/search?SQRY=ITNO:${encodeURIComponent(query + '*')} STAT:${encodeURIComponent('20')}&company=${encodeURIComponent(company)}&division=${encodeURIComponent(division)}`;
       const url = `${this.baseUrl}m3/item/search?SQRY=WHLO:${encodeURIComponent(warehouse)} AND ITNO:${encodeURIComponent(query)}  STAT:${encodeURIComponent('20')} &company=${encodeURIComponent(company)}&division=${encodeURIComponent(division)}`;

        return this.http.get<any[]>(url).pipe(catchError((err) => throwError(() => err)));
    }

    /**
     * V2 unified item search (name or number) via ITEM_SEARCH.
     * SQRY format matches UAT: WHLO + STAT:20 + related:[ITEM_SEARCH("query*")].
     */
    searchItemsV2(query: string, company: string, division: string, warehouse: string): Observable<any[]> {
        const sqry = `WHLO%3A${encodeURIComponent(warehouse)}%20%20%20STAT%3A20%20related%3A%5BITEM_SEARCH(%22${encodeURIComponent(query)}%22)%5D`;
        const url = `${this.baseUrl}m3/item/search?SQRY=${sqry}&company=${encodeURIComponent(company)}&division=${encodeURIComponent(division)}&warehouse=${encodeURIComponent(warehouse)}`;
        return this.http.get<any[]>(url).pipe(catchError((err) => throwError(() => err)));
    }

    addOrderHeader(headerBody: AddOrderHeaderRequest, company: string, division: string): Observable<any> {
        const url = `${this.baseUrl}Order/AddOrderHeader?orderType=CustomerOrder&company=${encodeURIComponent(company)}&division=${encodeURIComponent(division)}`;
        return this.http.post<any>(url, headerBody).pipe(catchError((err) => throwError(() => err)));
    }

    updateOrderHeaderUca2(orderNumber: string, company: string, division: string, uca2: string): Observable<string> {
        const url = `${this.baseUrl}Order/UpdateOrderHeader/Uca2?orderType=CustomerOrder&company=${encodeURIComponent(company)}&division=${encodeURIComponent(division)}&orderNumber=${encodeURIComponent(orderNumber)}`;
        return this.http.put(url, { uca2 }, { responseType: 'text' }).pipe(catchError((err) => throwError(() => err)));
    }

    updateOrderHeaderTextByDocuments(
        orderNumber: string,
        company: string,
        division: string,
        textType: string,
        targets: { key: string; label: string; text: string }[]
    ): Observable<any> {
        const url = `${this.baseUrl}Order/UpdateOrderHeader/TextByDocuments?orderType=CustomerOrder&company=${encodeURIComponent(company)}&division=${encodeURIComponent(division)}&orderNumber=${encodeURIComponent(orderNumber)}`;
        const payload = {
            textType,
            targets: targets.map((target) => ({
                documentClass: target.key,
                documentName: target.label,
                text: target.text
            }))
        };
        return this.http.put<any>(url, payload).pipe(catchError((err) => throwError(() => err)));
    }
    updateOrderLineTextByDocuments(
        orderNumber: string,
        lineNo: string,
        company: string,
        division: string,
        textType: string,
        targets: { key: string; label: string; text: string }[]
    ): Observable<string> {
        const url = `${this.baseUrl}Order/UpdateOrderLine/TextByDocuments?orderType=CustomerOrder&company=${encodeURIComponent(company)}&division=${encodeURIComponent(division)}&orderNumber=${encodeURIComponent(orderNumber)}`;
        const payload = {
            lineNo,
            textType,
            targets: targets.map((target) => ({
                documentClass: target.key,
                documentName: target.label,
                text: target.text
            }))
        };
        return this.http.put(url, payload, { responseType: 'text' }).pipe(catchError((err) => throwError(() => err)));
    }

    queuePendingHeaderText(textType: string, targets: { key: string; label: string; text: string }[]): void {
        this.pendingHeaderTextEntries.push({ textType, targets });
    }

    queuePendingLineText(lineNo: string, textType: string, targets: { key: string; label: string; text: string }[]): void {
        this.pendingLineTextEntries.push({ lineNo, textType, targets });
    }

    /** True when a non–Extra-Info HEADER_PRE_TEXT is pending (manual Pre-Text). */
    hasPendingManualHeaderPreText(): boolean {
        return this.pendingHeaderTextEntries.some(
            (e) => e.textType === 'HEADER_PRE_TEXT' && !e.fromExtraInfo
        );
    }

    hasPendingExtraInfoHeaderPreText(): boolean {
        return this.pendingHeaderTextEntries.some(
            (e) => e.textType === 'HEADER_PRE_TEXT' && e.fromExtraInfo
        );
    }

    /**
     * Queue/replace HEADER_PRE_TEXT from Extra information (uca2) for picking list only (CO04).
     * Does not remove manually queued Pre-Text entries.
     */
    upsertPendingHeaderPreTextFromExtraInfo(text: string): void {
        const trimmed = (text || '').trim();
        this.pendingHeaderTextEntries = this.pendingHeaderTextEntries.filter(
            (e) => !(e.textType === 'HEADER_PRE_TEXT' && e.fromExtraInfo)
        );
        if (!trimmed) {
            return;
        }
        this.pendingHeaderTextEntries.push({
            textType: 'HEADER_PRE_TEXT',
            fromExtraInfo: true,
            targets: [{ key: 'CO04', label: 'On the picking list', text: trimmed }]
        });
    }

    clearPendingHeaderPreTextFromExtraInfo(): void {
        this.pendingHeaderTextEntries = this.pendingHeaderTextEntries.filter(
            (e) => !(e.textType === 'HEADER_PRE_TEXT' && e.fromExtraInfo)
        );
    }

    flushPendingPrePostTexts(orderNumber: string, company: string, division: string): Observable<string[]> {
        const requests: Observable<string>[] = [];

        for (const entry of this.pendingHeaderTextEntries) {
            requests.push(
                this.updateOrderHeaderTextByDocuments(orderNumber, company, division, entry.textType, entry.targets).pipe(
                    map((res: any) => (typeof res === 'string' ? res : 'Header text updated successfully.')),
                    catchError((err) => of(err?.error?.message || 'Failed to save pending header text.'))
                )
            );
        }

        for (const entry of this.pendingLineTextEntries) {
            if (!entry.lineNo) continue;
            requests.push(
                this.updateOrderLineTextByDocuments(orderNumber, entry.lineNo, company, division, entry.textType, entry.targets).pipe(
                    map((res: any) => (typeof res === 'string' ? res : 'Line text updated successfully.')),
                    catchError((err) => of(err?.error?.message || `Failed to save pending line text for line ${entry.lineNo}.`))
                )
            );
        }

        this.pendingHeaderTextEntries = [];
        this.pendingLineTextEntries = [];

        if (!requests.length) {
            return of([]);
        }
        return forkJoin(requests);
    }

    uploadBulkOrderLines(payload: any, company: string, division: string): Observable<any> {
        const url = `${this.baseUrl}OrderLine/UploadBulkOrderLines?orderType=CustomerOrderLine&company=${encodeURIComponent(company)}&division=${encodeURIComponent(division)}`;
        return this.http.post<any>(url, payload, { responseType: 'text' as 'json' }).pipe(catchError((err) => throwError(() => err)));
    }
    releaseOrderLock(orderNumber: string, companyId: string, divisionId: string, currentUsername: string): Observable<any> {
        const url = `${environment.apiUrl}orders/lock/release?orderNumber=${orderNumber}&companyId=${companyId}&divisionId=${divisionId}&currentUsername=${currentUsername}`;

        // const url = `http://192.168.31.92:8444/orders/lock/release?orderNumber=${orderNumber}&companyId=${companyId}&divisionId=${divisionId}&currentUsername=${currentUsername}`;
        return this.http.post(url, {}); // POST with empty body
    }
    refreshOrderLock(orderNumber: string, companyId: string, divisionId: string, currentUsername: string): Observable<any> {
        const url = `${environment.apiUrl}orders/lock/lockrefresh?orderNumber=${orderNumber}&companyId=${companyId}&divisionId=${divisionId}&currentUsername=${currentUsername}`;

        // const url = `http://192.168.31.92:8444/orders/lock/lockrefresh?orderNumber=${orderNumber}&companyId=${companyId}&divisionId=${divisionId}&currentUsername=${currentUsername}`;
        return this.http.post(url, {}); // POST with empty body
    }
    getPriceListInfo(customerId: string, companyId: string, divisionId: string, currentUsername: string): Observable<any> {
        const url = `${environment.apiUrl}m3/SearchCustomer/Details?customerId=${customerId}&company=${companyId}&division=${divisionId}&faci=${currentUsername}`;
        // Attach Bearer explicitly — avoids 401 when interceptor refresh races on page load.
        return this.http.get(url, getAuthHttpOptions());
    }

    /**
     * Fetch item-level cost and minimum margin details
     * Used for individual item margin validation during item addition
     */
    getItemMarginDetails(company: string, division: string, itemNumber: string, customerId: string): Observable<any> {
        const url = `${this.baseUrl}m3/item/margindetails?company=${encodeURIComponent(company)}&division=${encodeURIComponent(division)}`;
        const payload = { itemNumber, customerId };
        console.log('📡 [API CALL] Fetching item margin details:', { url, payload });
        return this.http.post<any>(url, payload).pipe(
            catchError((err) => {
                console.error('❌ [API ERROR] Item margin details fetch failed:', err);
                return throwError(() => err);
            })
        );
    }

    /**
     * Fetch order-level minimum margin requirement
     * Used for order-level validation before final submission
     */
    getOrderMarginDetails(customerId: string, company: string, division: string): Observable<any> {
        const url = `${this.baseUrl}m3/order/margindetails?customerId=${encodeURIComponent(customerId)}&company=${company}&division=${division}`;
        console.log('📡 [API CALL] Fetching order margin details:', { url, customerId, company, division });
        return this.http.get<any>(url).pipe(
            catchError((err) => {
                console.error('❌ [API ERROR] Order margin details fetch failed:', err);
                return throwError(() => err);
            })
        );
    }

    /** Refresh M3 line status for a synced order (LstCOLineInfo → m3LineStatus). */
    refreshM3LineStatuses(orderNumber: string, company: string, division: string): Observable<{
        orderNumber: string;
        referenceOrderId: string;
        updatedLines: number;
        skippedLines: number;
        message: string;
    }> {
        const url =
            `${this.baseUrl}Order/RefreshM3LineStatuses` +
            `?orderNumber=${encodeURIComponent(orderNumber)}` +
            `&company=${encodeURIComponent(company || '')}` +
            `&division=${encodeURIComponent(division || '')}`;
        return this.http.post<{
            orderNumber: string;
            referenceOrderId: string;
            updatedLines: number;
            skippedLines: number;
            message: string;
        }>(url, {}).pipe(catchError((err) => throwError(() => err)));
    }

    /** Post-sync price update: OIS100MI/UpdPriceInfo for changed lines, then Torna DB. */
    updatePricesInM3(payload: {
        company: string;
        division: string;
        orderNumber: string;
        m3OrderNumber: string;
        lines: Array<{
            orderLineId?: number | null;
            lineNo: string;
            rate: number;
            discount: number;
            previousRate: number;
            previousDiscount: number;
        }>;
    }): Observable<any> {
        const url = `${this.baseUrl}OrderLine/UpdatePricesInM3`;
        return this.http.post<any>(url, payload).pipe(catchError((err) => throwError(() => err)));
    }
}

type VatBucket = {
    DIVI?: string;
    VTP1?: string;
    FRDT?: string;
    CSCD?: string;
    WHLO?: string;
};

function text(value: unknown): string {
    return String(value ?? '').trim();
}

/** Warehouse 450 is intentionally unmapped. */
export function resolveVatFieldsByWarehouse(lst: any, warehouse: string): VatBucket {
    const whlo = String(warehouse ?? '').trim();
    if (!lst || !whlo) {
        return {};
    }

    const buckets: { match: string; fields: VatBucket }[] = [
        {
            match: text(lst.MBWHLO),
            fields: { DIVI: lst.CVDIVI, VTP1: lst.CVVTP1, FRDT: lst.CVFRDT, CSCD: lst.CVCSCD, WHLO: lst.MBWHLO }
        },
        {
            match: text(lst.M2WHLO),
            fields: { DIVI: lst.C3DIVI, VTP1: lst.C3VTP1, FRDT: lst.C3FRDT, CSCD: lst.C3CSCD, WHLO: lst.M2WHLO }
        },
        {
            match: text(lst.M3WHLO),
            fields: { DIVI: lst.C4DIVI, VTP1: lst.C4VTP1, FRDT: lst.C4FRDT, CSCD: lst.C4CSCD, WHLO: lst.M3WHLO }
        },
        {
            match: text(lst.M4WHLO),
            fields: { DIVI: lst.C2DIVI, VTP1: lst.C2VTP1, FRDT: lst.C2FRDT, CSCD: lst.C2CSCD, WHLO: lst.M4WHLO }
        },
        {
            match: text(lst.M5WHLO),
            fields: { DIVI: lst.C1DIVI, VTP1: lst.C1VTP1, FRDT: lst.C1FRDT, CSCD: lst.C1CSCD, WHLO: lst.M5WHLO }
        }
    ];

    const matched = buckets.find((bucket) => bucket.match && bucket.match === whlo);
    if (matched) {
        return matched.fields;
    }

    switch (whlo) {
        case '101':
            return { DIVI: lst.CVDIVI, VTP1: lst.CVVTP1, FRDT: lst.CVFRDT, CSCD: lst.CVCSCD, WHLO: lst.MBWHLO };
        case '110':
            return { DIVI: lst.C3DIVI, VTP1: lst.C3VTP1, FRDT: lst.C3FRDT, CSCD: lst.C3CSCD, WHLO: lst.M2WHLO };
        case '130':
            return { DIVI: lst.C4DIVI, VTP1: lst.C4VTP1, FRDT: lst.C4FRDT, CSCD: lst.C4CSCD, WHLO: lst.M3WHLO };
        case '300':
            return { DIVI: lst.C2DIVI, VTP1: lst.C2VTP1, FRDT: lst.C2FRDT, CSCD: lst.C2CSCD, WHLO: lst.M4WHLO };
        case '400':
            return { DIVI: lst.C1DIVI, VTP1: lst.C1VTP1, FRDT: lst.C1FRDT, CSCD: lst.C1CSCD, WHLO: lst.M5WHLO };
        default:
            return {};
    }
}
